import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface DealProps {
  id?: string;
  image: string;
  title: string;
  originalPrice: string;
  salePrice: string;
  discount: string;
  store: string;
  link: string;
  delay: number;
}

interface UseShoppingDealsReturn {
  deals: DealProps[];
  isLoading: boolean;
  error: string | null;
}

// Dữ liệu mẫu sẽ được sử dụng nếu có lỗi khi tải từ Supabase
const MOCK_DEALS: DealProps[] = [
  {
    image:
      "https://images.pexels.com/photos/3685530/pexels-photo-3685530.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Premium Vitamin C Serum for Brighter Skin",
    originalPrice: "$89.99",
    salePrice: "$59.99",
    discount: "33%",
    store: "Sephora",
    link: "#",
    delay: 0.1,
  },
  {
    image:
      "https://images.pexels.com/photos/904350/pexels-photo-904350.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Designer Crossbody Bag - Elegant Collection",
    originalPrice: "$149.99",
    salePrice: "$99.99",
    discount: "33%",
    store: "Amazon",
    link: "#",
    delay: 0.2,
  },
  {
    image:
      "https://images.pexels.com/photos/8989481/pexels-photo-8989481.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Luxury Silk Pillowcase Set - Anti-Aging",
    originalPrice: "$79.99",
    salePrice: "$49.99",
    discount: "38%",
    store: "Target",
    link: "#",
    delay: 0.3,
  },
  {
    image:
      "https://images.pexels.com/photos/5184412/pexels-photo-5184412.jpeg?auto=compress&cs=tinysrgb&w=800",
    title: "Smart Fitness Watch with Heart Rate Monitor",
    originalPrice: "$199.99",
    salePrice: "$139.99",
    discount: "30%",
    store: "Walmart",
    link: "#",
    delay: 0.4,
  },
];

export const useShoppingDeals = (): UseShoppingDealsReturn => {
  const [deals, setDeals] = useState<DealProps[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeals = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Lấy dữ liệu từ bảng deals trong Supabase
        const { data, error } = await supabase
          .from("deals")
          .select("*")
          .eq("active", true)
          .order("created_at", { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          // Chuyển đổi dữ liệu từ Supabase sang định dạng DealProps và thêm delay
          const dealsWithDelay = data.map((deal, index) => ({
            id: deal.id,
            title: deal.title,
            image: deal.image,
            originalPrice: deal.original_price,
            salePrice: deal.sale_price,
            discount: deal.discount,
            store: deal.store,
            link: deal.link,
            delay: index * 0.1,
          }));

          setDeals(dealsWithDelay);
        } else {
          // Nếu không có dữ liệu, sử dụng dữ liệu mẫu
          console.warn("Không có deals từ Supabase, sử dụng dữ liệu mẫu.");
          setDeals(MOCK_DEALS);
        }
      } catch (error) {
        console.error("Lỗi khi tải dữ liệu deals:", error);
        setError("Không thể tải danh sách deal. Vui lòng thử lại sau.");
        // Fallback to mock data
        setDeals(MOCK_DEALS);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeals();
  }, []);

  return {
    deals,
    isLoading,
    error,
  };
};

export default useShoppingDeals;
